import { aiChatQueryKeys } from "@/lib/data/react-query/ai-chat";
import type {
  AiChatStreamConnection,
  AiChatStreamEvent,
  ChatApi,
} from "@/lib/types/ai-chat";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { applyAiChatStreamEvent } from "./ai-chat-cache";

export type AiChatStreamStatus =
  | "stopped"
  | "connecting"
  | "ready"
  | "reconnecting"
  | "error";

export const useAiChatStream = ({
  api,
  runId,
  threadId,
  refreshLatest,
  onClosed,
}: {
  api: ChatApi;
  runId?: string;
  threadId?: string;
  refreshLatest: () => Promise<void>;
  onClosed: () => void;
}) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AiChatStreamStatus>("stopped");
  const [error, setError] = useState<Error | null>(null);
  const [restartVersion, setRestartVersion] = useState(0);
  const restart = useCallback(() => {
    setRestartVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    setError(null);
    if (!runId || !threadId) {
      setStatus("stopped");
      return;
    }
    let connection: AiChatStreamConnection | undefined;
    let cursor: string | null = null;
    let disposed = false;
    let reconnectAttempt = 0;
    let reconnectTimer: number | undefined;
    let terminal = false;
    let connectionVersion = 0;
    let refreshPending = false;

    const scheduleReconnect = () => {
      if (disposed || terminal) return;
      setStatus("reconnecting");
      const delay = Math.min(1_000 * 2 ** reconnectAttempt, 30_000);
      reconnectAttempt += 1;
      window.clearTimeout(reconnectTimer);
      reconnectTimer = window.setTimeout(() => void connect(), delay);
    };

    const handleEvent = (event: AiChatStreamEvent) => {
      if (event.type === "thread.error") {
        setError(new Error(event.detail.message));
        return;
      }
      reconnectAttempt = 0;
      setError(null);
      if (event.type === "thread.snapshot") cursor = event.cursor;
      if (event.type === "thread.changes") cursor = event.nextCursor;
      applyAiChatStreamEvent(queryClient, event);
      if (event.type === "thread.closed") {
        terminal = true;
        cursor = event.cursor;
        setStatus("stopped");
        onClosed();
        void queryClient.invalidateQueries({
          queryKey: aiChatQueryKeys.threadLists(),
        });
        void queryClient.invalidateQueries({
          queryKey: aiChatQueryKeys.thread(threadId),
        });
        void queryClient.invalidateQueries({
          queryKey: aiChatQueryKeys.timeline(threadId),
        });
        return;
      }
      if (event.type === "thread.changes" && event.hasMore && !refreshPending) {
        refreshPending = true;
        // A truncated batch can contain only the prefix of a paused run's
        // updates. Fetch canonical state without waiting for another event.
        void refreshLatest()
          .catch((cause: unknown) => {
            if (!disposed) {
              setError(
                cause instanceof Error
                  ? cause
                  : new Error("The latest conversation could not be loaded."),
              );
            }
          })
          .finally(() => {
            refreshPending = false;
          });
      }
    };

    const connect = async () => {
      if (disposed || terminal) return;
      const version = ++connectionVersion;
      const isCurrent = () =>
        !disposed && !terminal && version === connectionVersion;
      connection?.close();
      connection = undefined;
      setStatus(reconnectAttempt ? "reconnecting" : "connecting");
      try {
        const nextConnection = await api.openRunStream({
          runId,
          cursor,
          limit: 100,
          onOpen: () => {
            if (isCurrent()) {
              setError(null);
              setStatus("ready");
            }
          },
          onEvent: (event) => {
            if (isCurrent()) handleEvent(event);
          },
          onError: (streamError) => {
            if (isCurrent()) setError(streamError);
          },
          onClose: () => {
            // Only thread.closed confirms completion, even after a normal close.
            if (isCurrent() && !terminal) scheduleReconnect();
          },
        });
        if (!isCurrent()) nextConnection.close();
        else connection = nextConnection;
      } catch (cause) {
        if (!isCurrent()) return;
        setError(
          cause instanceof Error ? cause : new Error("Chat stream failed."),
        );
        scheduleReconnect();
      }
    };

    void connect();
    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      connection?.close();
    };
  }, [
    api,
    onClosed,
    queryClient,
    refreshLatest,
    runId,
    threadId,
    restartVersion,
  ]);

  return { status, error, restart };
};
